import axios                    from 'axios';
import { useCallback, useMemo } from 'react';
import { useTranslation }       from 'react-i18next';
import DV                       from 'ts-data-validator';

import errorConstants from './constants';

import { objectToFormData } from '../../tools/object-to-formdata';
import { useAlert }         from '../../../providers/alert';

const api = axios.create({
  headers:         {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type':     'multipart/form-data',
  },
  withCredentials: true,
});

function useRequest(axiosConfig = {}) {
  const { t }         = useTranslation();
  const { showAlert } = useAlert();

  const getConfig = useCallback((config = {}) => {
    return {
      ...axiosConfig,
      ...config,
      headers: {
        ...(axiosConfig.headers || {}),
        ...(config.headers || {}),
      },
    };
  }, [axiosConfig]);

  const prepareDate = useCallback((data) => {
    return objectToFormData(data, {
      indices: true,
      nulls:   false,
    });
  }, []);

  const processFailure = useCallback((error) => {
    switch (error.errorType) {
      case errorConstants.SERVER_ERROR:
        showAlert('error', t('SERVER_ERROR'));
        return Promise.reject(t('SERVER_ERROR'));
      case errorConstants.WRONG_RESPONSE_ERROR:
        showAlert('error', t('WRONG_RESPONSE_ERROR'));
        return Promise.reject(t('WRONG_RESPONSE_ERROR'));
      case errorConstants.NOT_FOUND_ERROR:
        showAlert('error', t('NOT_FOUND_ERROR'));
        return Promise.reject(t('NOT_FOUND_ERROR'));
      case errorConstants.NOT_AUTHORIZED_ERROR:
        showAlert('error', t('NOT_AUTHORIZED_ERROR'));
        return Promise.reject(t('NOT_AUTHORIZED_ERROR'));
      case errorConstants.COMMON_ERROR:
        showAlert(error.messageType, error.message);
        return Promise.reject(error);
      case errorConstants.VALIDATION_ERROR:
        return Promise.reject(error);
      default:
        break;
    }
  }, []);

  const processResponse = useCallback((promise) => promise
      .then((response) => {
        const {
                data,
                status,
              } = response;

        if (status === 200) {
          const {
                  success,
                  errors,
                  vars,
                  messageType,
                  message,
                  redirect,
                } = data;
          if (
            DV.isUndefined(success) ||
            DV.isUndefined(errors) ||
            DV.isUndefined(errors.common) ||
            DV.isUndefined(errors.validation) ||
            DV.isUndefined(vars) ||
            DV.isUndefined(messageType) ||
            DV.isUndefined(message) ||
            DV.isUndefined(redirect)
          ) {
            return Promise.reject({ errorType: errorConstants.WRONG_RESPONSE_ERROR });
          }

          if (success) {
            return response;
          }

          if (errors.common) {
            return Promise.reject({
              errorType: errorConstants.COMMON_ERROR,
              messageType,
              message:   errors.common,
            });
          }

          if (DV.isNotEmptyObject(errors.validation)) {
            return Promise.reject({
              errorType: errorConstants.VALIDATION_ERROR,
              message:   errorConstants.VALIDATION_ERROR,
              messageType,
              errors:    errors.validation,
            });
          }
        }

        throw Error;
      })
      .catch((error) => {
        if (error instanceof Error) {
          switch (error.errorType) {
            case 'Request failed with status code 404':
              return Promise.reject({ errorType: errorConstants.NOT_FOUND_ERROR });
            case 'Request failed with status code 403':
              return Promise.reject({ errorType: errorConstants.NOT_AUTHORIZED_ERROR });
            case 'Request aborted':
              break;
            case errorConstants.VALIDATION_ERROR:
              return Promise.reject({
                errorType: errorConstants.VALIDATION_ERROR,
                errors:    error.errors,
              });
            default:
              return Promise.reject({ errorType: errorConstants.SERVER_ERROR });
          }
        }
        return Promise.reject(error);
      })
    , []);

  const processSuccess = useCallback((response) => {
    const {
            data: {
                    vars,
                    message,
                    redirect,
                  },
          } = response;

    if (redirect && !isSSR()) {
      window.location.href = redirect;
      return;
    }

    if (message) {
      showAlert('success', message);
    }
    return vars;
  }, []);

  const process = useCallback((promise) => {
    return processResponse(promise)
      .then(processSuccess)
      .catch(processFailure);
  }, [processFailure, processResponse, processSuccess]);

  return useMemo(() => ({
    get(url, config) {
      return process(
        api.get(url, config),
      );
    },
    post(url, data, config) {
      return process(
        api.post(url, prepareDate(data), getConfig(config)),
      );
    },
  }), [getConfig, process]);
}

export default useRequest;